import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Seo, breadcrumbSchema, webPageSchema } from '../components/Seo';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import Reveal from '../components/Reveal';
import RegisterGate from '../components/RegisterGate';
import { PILLARS } from '../data/content';
import { ArrowUpRight, ArrowLeft, Check, Upload, X, FileText, Mail, Phone } from 'lucide-react';
import { nominatorApi, useNominatorAuth } from '@/context/NominatorAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENTRANT_TYPES = [
  'Brand',
  'Creative Agency',
  'Media Agency',
  'Digital Agency',
  'Influencer Marketing Agency',
  'PR & Communications Agency',
  'Production House / Content Studio',
  'Sports Marketing Agency',
  'Independent Creator',
  'Other',
];

const ALL_CATEGORIES = PILLARS.flatMap((p) =>
  p.categories.map((c) => ({ pillar: p.name, name: c.name, label: `${p.name} - ${c.name}` }))
);

const INITIAL = {
  // 1
  entrant_type: '', entrant_type_other: '',
  organisation_name: '', first_name: '', last_name: '', designation: '',
  email: '', mobile: '', website: '', linkedin: '',
  // 2
  category: '', additional_categories: [],
  entry_title: '', brand_name: '',
  campaign_start: '', campaign_end: '', primary_market: '',
  // 3
  campaign_summary: '', business_challenge: '', strategy_idea: '',
  execution: '', elevator_pitch: '',
  // 4
  campaign_objectives: '',
  key_results: ['', '', '', '', ''],
  business_impact: '',
  // 5
  credits_brand: '', credits_creative: '', credits_media: '',
  credits_digital: '', credits_influencer: '', credits_pr: '',
  credits_production: '', credits_tech: '', credits_other: '',
  // 6 — up to three case study uploads. The first slot is the primary
  // (25 MB); the other two are optional supplementary decks (15 MB each).
  case_study_deck_url: '', case_study_deck_name: '',
  case_study_deck_url_2: '', case_study_deck_name_2: '',
  case_study_deck_url_3: '', case_study_deck_name_3: '',
  video_link: '',
  supporting_material_link: '', previous_recognition: '',
  // 7
  brand_approval_url: '', brand_approval_name: '',
  // Brand-approval declaration was removed from the visible form on 27 Feb 2026.
  // We still send `declaration_agreed: true` so the backend (which persists the
  // flag for audit purposes) accepts the payload without a schema change.
  declaration_agreed: true,
};

// Per-slot upload size caps.
const DECK_LIMITS_MB = { deck: 25, deck2: 15, deck3: 15, approval: 15 };
// Broad accept list — the P0 requirement is "commonly used formats".
const DECK_ACCEPT = ".pdf,.ppt,.pptx,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png";
const APPROVAL_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

// A cheap heuristic that flags common "private link" shapes so the entrant
// gets an inline warning to re-share as publicly accessible. Not authoritative
// — we deliberately don't block the submit (some legit links look like this).
const looksPrivate = (url) => {
  const u = String(url || '').toLowerCase();
  if (!u) return null;
  if (/docs\.google\.com\/(document|spreadsheets|presentation|forms)\/.+\/edit/.test(u)) return "Google Docs edit links are usually private. Please share the link with 'Anyone with the link can view'.";
  if (/drive\.google\.com\/file\/d\/[^/]+\/edit/.test(u)) return "Google Drive edit URLs are typically private. Please switch to a 'shareable link' (Anyone with the link, Viewer).";
  if (/drive\.google\.com\/folderview/.test(u)) return "This Google Drive folder link looks private. Please share as 'Anyone with the link can view'.";
  if (/onedrive\.live\.com\/edit\?/.test(u)) return "OneDrive edit URLs are private. Please copy the 'Share' link (Anyone with the link).";
  if (/dropbox\.com\/scl\/fi\/[^?]+\?/.test(u) && !/dl=0|dl=1/.test(u)) return "Dropbox link may require sign-in. Please use the public 'Copy link' from the Share menu.";
  return null;
};

const wordCount = (s) => (s ? s.trim().split(/\s+/).filter(Boolean).length : 0);

const EnterNow = ({ portalMode = false }) => {
  const navigate = useNavigate();
  const { nominator, loading: authLoading } = useNominatorAuth();
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState({ deck: false, deck2: false, deck3: false, approval: false });
  const [uploadProgress, setUploadProgress] = useState({ deck: 0, deck2: 0, deck3: 0, approval: 0 });
  const [checkoutPreview, setCheckoutPreview] = useState(null);
  // Wave-2: split the long form into 3 collapsible tab-sections with a
  // sticky left-side navigator. Users can freely jump; validation only
  // enforces required fields at final Submit (soft nav — no gating).
  const [activeSection, setActiveSection] = useState(1);
  const [savingDraft, setSavingDraft] = useState(false);
  // Wave-3: track the persisted draft id so Save Draft becomes idempotent
  // (updates the same row instead of creating a new draft each time).
  const [draftId, setDraftId] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle | saving | saved | error
  const autoSaveTimer = useRef(null);
  const initializedFromDraft = useRef(false);
  const [isEditingSubmitted, setIsEditingSubmitted] = useState(false);

  // Per-section required-field completion signal — powers the sidebar
  // check indicator so entrants see at a glance what's left. Kept as
  // pure derivations so it recomputes on every keystroke.
  const s1Required = ['entrant_type', 'organisation_name', 'first_name', 'last_name', 'designation', 'email', 'mobile', 'website', 'category', 'entry_title', 'brand_name', 'campaign_start', 'campaign_end', 'primary_market'];
  const s2Required = ['campaign_summary', 'business_challenge', 'strategy_idea', 'execution', 'elevator_pitch', 'campaign_objectives', 'business_impact'];
  const sectionComplete = {
    1: s1Required.every((k) => String(form[k] || '').trim().length > 0),
    2: s2Required.every((k) => String(form[k] || '').trim().length > 0),
    3: true,
  };

  // Load draft or submitted entry from ?draft_id=... — allows Resume from
  // My Entries and Edit-after-submission (before deadline).
  useEffect(() => {
    if (!nominator || initializedFromDraft.current) return;
    let ignore = false;
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const id = params.get('draft_id');
        if (!id) return;
        const { data } = await nominatorApi.get(`/nominator/nominations/${id}`);
        if (!data || ignore) return;
        // Hydrate the form from the persisted doc. The stored doc uses the
        // "canonical" field names (case_study_deck_url etc.) — map back.
        setForm((f) => ({
          ...f,
          entrant_type: data.entrant_type || '',
          entrant_type_other: data.entrant_type_other || '',
          organisation_name: data.organisation_name || '',
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          designation: data.designation || '',
          email: data.email || '',
          mobile: data.mobile || '',
          website: data.website || '',
          linkedin: data.linkedin || '',
          category: data.category || data.primary_category || '',
          additional_categories: data.additional_categories || [],
          entry_title: data.entry_title || '',
          brand_name: data.brand_name || '',
          campaign_start: data.campaign_start || '',
          campaign_end: data.campaign_end || '',
          primary_market: data.primary_market || '',
          campaign_summary: data.campaign_summary || '',
          business_challenge: data.business_challenge || '',
          strategy_idea: data.strategy_idea || '',
          execution: data.execution || '',
          elevator_pitch: data.elevator_pitch || '',
          campaign_objectives: data.campaign_objectives || '',
          key_results: Array.isArray(data.key_results) && data.key_results.length
            ? [...data.key_results, '', '', '', '', ''].slice(0, 5)
            : ['', '', '', '', ''],
          business_impact: data.business_impact || '',
          credits_brand: data.credits_brand || '',
          credits_creative: data.credits_creative || '',
          credits_media: data.credits_media || '',
          credits_digital: data.credits_digital || '',
          credits_influencer: data.credits_influencer || '',
          credits_pr: data.credits_pr || '',
          credits_production: data.credits_production || '',
          credits_tech: data.credits_tech || '',
          credits_other: data.credits_other || '',
          case_study_deck_url: data.case_study_deck_url || '',
          case_study_deck_name: data.case_study_deck_name || '',
          case_study_deck_url_2: data.case_study_deck_url_2 || '',
          case_study_deck_name_2: data.case_study_deck_name_2 || '',
          case_study_deck_url_3: data.case_study_deck_url_3 || '',
          case_study_deck_name_3: data.case_study_deck_name_3 || '',
          video_link: data.video_link || '',
          supporting_material_link: data.supporting_material_link || '',
          previous_recognition: data.previous_recognition || data.additional_notes || '',
          brand_approval_url: data.brand_approval_url || '',
          brand_approval_name: data.brand_approval_name || '',
          // Force true — the declaration checkbox was removed on 27 Feb 2026.
          declaration_agreed: true,
        }));
        setDraftId(id);
        // `isEditingSubmitted` is TRUE for any existing entry the user is
        // editing — both submitted (paid online) AND payment_pending (offline
        // awaiting verification). We treat both the same for the edit UX:
        // PATCH updates, "Save Changes" button, redirect to entries list
        // (no cart step, no re-payment). The name is legacy — kept to avoid
        // a wide rename; scope is "editing an existing non-draft entry".
        setIsEditingSubmitted(data.status === 'submitted' || data.status === 'payment_pending');
        initializedFromDraft.current = true;
      } catch (err) {
        // 404 or 401 — clear the param so we don't loop
        toast.error('Could not load that draft.');
      }
    })();
    return () => { ignore = true; };
  }, [nominator]);

  // Save as Draft — persists the current form state without triggering a
  // Razorpay flow. Uses the existing `/nominator/nominations` endpoint which
  // already accepts partial data and stamps status='draft'. Once a draft
  // exists, subsequent saves PATCH the same row so we don't fan out.
  const saveDraft = async ({ silent = false } = {}) => {
    if (!nominator) {
      if (!silent) toast.error('Please sign in first.');
      return;
    }
    if (!form.entry_title.trim() && !form.brand_name.trim() && !form.category.trim()) {
      if (!silent) toast.error('Add at least a category, entry title or brand name before saving a draft.');
      return;
    }
    if (!silent) setSavingDraft(true);
    setAutoSaveStatus('saving');
    try {
      const payload = { ...form, status: isEditingSubmitted ? undefined : 'draft' };
      if (draftId) {
        const { data } = await nominatorApi.patch(`/nominator/nominations/${draftId}`, payload);
        if (data?.id) setDraftId(data.id);
      } else {
        const { data } = await nominatorApi.post('/nominator/nominations', payload);
        if (data?.id) setDraftId(data.id);
      }
      setAutoSaveStatus('saved');
      if (!silent) toast.success('Draft saved. You can pick it back up from My Entries any time.');
    } catch (err) {
      setAutoSaveStatus('error');
      const detail = err?.response?.data?.detail;
      if (!silent) toast.error(typeof detail === 'string' ? detail : 'Could not save draft. Please retry.');
    } finally {
      if (!silent) setSavingDraft(false);
    }
  };

  // Wave-3: Debounced auto-save. Fires 2s after the last change so users
  // never lose data if they refresh mid-form. Skips uploads-only changes
  // (form starts pristine), and only runs when we already have SOMETHING
  // worth saving.
  const formStr = JSON.stringify(form);
  useEffect(() => {
    if (!nominator) return;
    // Wait until we've either loaded a draft or the user has typed something.
    if (!form.entry_title && !form.brand_name && !form.category) return;
    // Do NOT auto-save when editing a submitted entry — the user should
    // explicitly click Save so submissions don't drift accidentally.
    if (isEditingSubmitted) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => { saveDraft({ silent: true }); }, 2000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [formStr, nominator]);

  // Public /enter is a marketing entry-point only. If we detect an active
  // nominator session, always redirect to the portal dashboard — the entry
  // form (/portal/new) must only be reachable via an explicit click from
  // inside the portal, never as a bookmarked public URL.
  useEffect(() => {
    if (!portalMode && !authLoading && nominator) {
      navigate('/portal/dashboard', { replace: true });
    }
  }, [portalMode, authLoading, nominator, navigate]);

  // Pre-fill from the authenticated nominator session (Company, Name, Email, Phone).
  useEffect(() => {
    if (nominator) {
      setForm((f) => ({
        ...f,
        organisation_name: f.organisation_name || nominator.organisation || '',
        first_name: f.first_name || (nominator.name || '').split(' ')[0] || '',
        last_name: f.last_name || (nominator.name || '').split(' ').slice(1).join(' ') || '',
        email: f.email || nominator.email || '',
        mobile: f.mobile || nominator.phone || '',
      }));
    }
  }, [nominator]);

  // Pre-fill category from URL ?category=...
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const cat = params.get('category');
      if (cat) {
        const match = ALL_CATEGORIES.find((c) => c.label === cat || c.value === cat);
        setForm((f) => ({ ...f, category: match ? match.label : cat }));
        // Scroll to category section after layout
        setTimeout(() => {
          const el = document.querySelector('[data-testid="field-category"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 350);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const set = (k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: '' }));
  };

  // Word-limit map (kept alongside `validate()` so both blur and submit
  // paths stay in lockstep).
  const wordLimits = {
    campaign_summary: 200, business_challenge: 300, strategy_idea: 500,
    execution: 500, elevator_pitch: 100, campaign_objectives: 300, business_impact: 500,
  };

  // Live per-field validator invoked on blur. Reuses the same rules as
  // full-form validate() so entrants get instant, consistent feedback the
  // moment they leave a field — not only when they click Submit.
  // Campaign Timeline is bounded: campaigns must have gone live from
  // 1-July-2025 onward, and cannot be dated in the future (the entrant must
  // have already run the work being submitted). Enforced at three layers:
  //   1. HTML `min`/`max` attrs on the <input type="date"> (native picker
  //      greys out disallowed days on modern browsers).
  //   2. Explicit JS check below (belt-and-braces for browsers where the
  //      user types the date manually / pastes it in).
  //   3. Cross-field rule: end must be on/after start.
  // Backend (server.py Nomination model) still trusts the string, so a
  // final future refinement would be to mirror this range check server-side.
  const CAMPAIGN_MIN_DATE = '2025-07-01';
  const _campaignMaxDate = () => new Date().toISOString().slice(0, 10);
  const _validateCampaignDate = (k, v) => {
    // Returns error string (or '') for a campaign_start / campaign_end value.
    if (!v) return '';
    // Native <input type="date"> emits YYYY-MM-DD. Reject anything else so a
    // pasted "07/15/2025" or garbage doesn't slip past the check.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return 'Enter a valid date';
    if (v < CAMPAIGN_MIN_DATE) return 'Campaign timeline starts from 1 Jul 2025';
    if (v > _campaignMaxDate())  return 'Cannot be a future date';
    // Cross-field: end must be on or after start.
    if (k === 'campaign_end' && form.campaign_start && v < form.campaign_start) {
      return 'End date cannot be before start date';
    }
    if (k === 'campaign_start' && form.campaign_end && form.campaign_end < v) {
      // Nudge the entrant so the mismatch surfaces even when they edit start
      // AFTER already setting an end. The error lands on end, not start,
      // since end is the field that's now invalid.
      setErrors((e) => ({ ...e, campaign_end: 'End date cannot be before start date' }));
    }
    return '';
  };

  const validateField = (k) => {
    const required = new Set([
      'entrant_type', 'organisation_name', 'first_name', 'last_name', 'designation',
      'email', 'mobile', 'website',
      'category', 'entry_title', 'brand_name', 'campaign_start', 'campaign_end', 'primary_market',
      'campaign_summary', 'business_challenge', 'strategy_idea', 'execution', 'elevator_pitch',
      'campaign_objectives', 'business_impact',
    ]);
    const v = String(form[k] ?? '').trim();
    let msg = '';
    if (required.has(k) && !v) msg = 'Required';
    else if (k === 'email' && v && !/^\S+@\S+\.\S+$/.test(v)) msg = 'Valid email required';
    else if (k === 'entrant_type_other' && form.entrant_type === 'Other' && !v) msg = 'Required';
    else if ((k === 'campaign_start' || k === 'campaign_end') && v) msg = _validateCampaignDate(k, v);
    else if (wordLimits[k] && v && wordCount(v) > wordLimits[k]) msg = `Exceeds ${wordLimits[k]} words`;
    setErrors((e) => ({ ...e, [k]: msg }));
  };

  const validate = () => {
    const e = {};
    const req = [
      'entrant_type', 'organisation_name', 'first_name', 'last_name', 'designation',
      'email', 'mobile', 'website',
      'category', 'entry_title', 'brand_name', 'campaign_start', 'campaign_end', 'primary_market',
      'campaign_summary', 'business_challenge', 'strategy_idea', 'execution', 'elevator_pitch',
      'campaign_objectives', 'business_impact',
      // supporting_material_link is now optional — the deck upload (+ optional
      // video link) is the entrant's primary asset. A supporting link is a nice
      // to have but shouldn't gate submission.
    ];
    req.forEach((k) => {
      if (!String(form[k] ?? '').trim()) e[k] = 'Required';
    });
    if (form.entrant_type === 'Other' && !form.entrant_type_other.trim())
      e.entrant_type_other = 'Required';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Valid email required';
    // Campaign Timeline bounds — mirror the field-level check so entrants
    // clicking Submit with a manually-pasted invalid date get the same
    // messaging.
    ['campaign_start', 'campaign_end'].forEach((k) => {
      const msg = _validateCampaignDate(k, String(form[k] || '').trim());
      if (msg) e[k] = msg;
    });
    // Word limits
    const limits = {
      campaign_summary: 200, business_challenge: 300, strategy_idea: 500,
      execution: 500, elevator_pitch: 100, campaign_objectives: 300, business_impact: 500,
    };
    Object.entries(limits).forEach(([k, max]) => {
      if (form[k] && wordCount(form[k]) > max) e[k] = `Exceeds ${max} words`;
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const upload = async (file, slot) => {
    if (!file) return;
    const capMb = DECK_LIMITS_MB[slot] || 15;
    if (file.size > capMb * 1024 * 1024) {
      const humanSize = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`File is ${humanSize} MB — this slot allows up to ${capMb} MB. Please compress or split before retrying.`);
      return;
    }
    setUploading((u) => ({ ...u, [slot]: true }));
    setUploadProgress((p) => ({ ...p, [slot]: 0 }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Deliberately NOT setting Content-Type: axios/the browser attach the
      // correct `multipart/form-data; boundary=...` header automatically when
      // the body is a FormData. Setting it manually strips the boundary and
      // the server can no longer parse the request — which surfaced to users
      // as an axios "network error" (no response received).
      const r = await nominatorApi.post(`/uploads`, fd, {
        timeout: 180000, // 3 min — big case-study decks on slow connections
        onUploadProgress: (evt) => {
          if (evt && evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadProgress((p) => ({ ...p, [slot]: pct }));
          }
        },
      });
      const url = r.data.url;
      const fname = r.data.filename;
      if (slot === 'deck')      { set('case_study_deck_url',   url); set('case_study_deck_name',   fname); }
      else if (slot === 'deck2'){ set('case_study_deck_url_2', url); set('case_study_deck_name_2', fname); }
      else if (slot === 'deck3'){ set('case_study_deck_url_3', url); set('case_study_deck_name_3', fname); }
      else if (slot === 'approval') { set('brand_approval_url', url); set('brand_approval_name', fname); }
      toast.success('Upload complete.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const code = err?.code;
      let msg;
      if (typeof detail === 'string') msg = detail;
      else if (code === 'ECONNABORTED') msg = 'Upload timed out. Please check your connection speed and retry — larger decks work best on Wi-Fi.';
      else if (status) msg = `Upload failed (HTTP ${status}). Please retry.`;
      else msg = 'Upload could not complete. Please retry — if it keeps failing, try a smaller file or a different browser.';
      toast.error(msg);
    } finally {
      setUploading((u) => ({ ...u, [slot]: false }));
      setUploadProgress((p) => ({ ...p, [slot]: 0 }));
    }
  };

  // Load Razorpay checkout script once. We do this lazily right before submit
  // rather than on mount so users who never reach the payment step don't pay
  // the network cost. Returns true when the global Razorpay object is ready.
  const ensureRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const openRazorpay = (payment, submission) => new Promise((resolve, reject) => {
    if (!payment || !window.Razorpay) {
      reject(new Error('Payment gateway unavailable. Please try again.'));
      return;
    }
    const categoryList = (submission.categories || []).join(' · ');
    const rzp = new window.Razorpay({
      key: payment.key_id,
      amount: payment.amount_paise,
      currency: payment.currency || 'INR',
      order_id: payment.order_id,
      name: 'AdAura Awards 2026',
      description: `${submission.categories.length} categor${submission.categories.length === 1 ? 'y' : 'ies'} · ${categoryList}`.slice(0, 120),
      image: '/adaura-logo-trim.png',
      prefill: {
        name: `${submission.submitter?.name || ''}`.trim(),
        email: submission.submitter?.email,
        contact: submission.submitter?.mobile,
      },
      theme: { color: '#9F6A2C' },
      notes: { submission_id: submission.id, categories: String(submission.categories.length) },
      handler: async (resp) => {
        try {
          await nominatorApi.post(`/nominator/submissions/${submission.id}/verify-payment`, {
            submission_id: submission.id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          resolve(resp);
        } catch (err) {
          reject(err);
        }
      },
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled. Your submission is saved as payment-pending and can be paid later from your dashboard.')),
      },
    });
    rzp.on('payment.failed', (resp) => {
      reject(new Error(resp?.error?.description || 'Payment failed. Please retry from your dashboard.'));
    });
    rzp.open();
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please complete the highlighted fields.');
      const firstKey = Object.keys(errors)[0];
      // Jump to the section containing the first invalid field so the
      // entrant doesn't have to hunt across the 3 tabs to find what's
      // missing. Section 1 owns entrant+entry basics, section 2 owns
      // campaign narrative, section 3 owns declaration/media.
      const S2_KEYS = new Set(['campaign_summary', 'business_challenge', 'strategy_idea', 'execution', 'elevator_pitch', 'campaign_objectives', 'business_impact']);
      const S3_KEYS = new Set(['declaration_agreed']);
      const jumpTo = firstKey
        ? (S3_KEYS.has(firstKey) ? 3 : S2_KEYS.has(firstKey) ? 2 : 1)
        : activeSection;
      if (jumpTo !== activeSection) setActiveSection(jumpTo);
      // Give React a beat to swap the panel before we try to scroll.
      setTimeout(() => {
        const el = firstKey && document.querySelector(`[data-testid="field-${firstKey}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
      return;
    }
    setLoading(true);
    try {
      // Wave-3: If we're editing a submitted entry, save updates via PATCH
      // and stop — no payment step needed.
      if (isEditingSubmitted && draftId) {
        await nominatorApi.patch(`/nominator/nominations/${draftId}`, { ...form });
        toast.success('Your submission has been updated.');
        setLoading(false);
        navigate('/portal/entries');
        return;
      }

      // Wave-3: New flow — save this category as a standalone draft, then
      // send the user to the dashboard cart to combine with other drafts
      // and check out (Razorpay OR Offline). Each category is now truly
      // independent — no more "additional_categories" on the same form.
      const payload = { ...form, status: 'draft' };
      let id = draftId;
      if (id) {
        await nominatorApi.patch(`/nominator/nominations/${id}`, payload);
      } else {
        const { data } = await nominatorApi.post('/nominator/nominations', payload);
        id = data?.id;
        setDraftId(id);
      }
      toast.success('Nomination saved. Open the cart to check out.');
      // Bring the user to the dashboard so they can pick more drafts (or
      // just pay for this one).
      navigate('/portal/dashboard?cart=1');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(typeof detail === 'string' ? detail : 'Submission failed. Check fields and try again.');
    } finally {
      setLoading(false);
    }
  };

  const confirmAndPay = async () => {
    if (!checkoutPreview) return;
    const { submission, payment } = checkoutPreview;
    setLoading(true);
    try {
      const ok = await ensureRazorpay();
      if (!ok) {
        toast.error('Could not load payment gateway. Please retry from your dashboard.');
        return;
      }
      await openRazorpay(payment, submission);
      toast.success(`Payment received — ${submission.categories.length} nomination${submission.categories.length === 1 ? '' : 's'} confirmed.`);
      setSuccess(true);
      setForm(INITIAL);
      setCheckoutPreview(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.message || 'Payment could not be completed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-enter">
      <Seo
        title="Enter the AdAura Awards 2026 · Nomination Form"
        description="Submit your nomination for the AdAura Awards 2026. Multi-category cart, autosave drafts, GST-compliant invoices. Entry fee INR 7,500 (ex-GST). Nominations open now."
        path="/enter"
        jsonLd={[
          webPageSchema({ path: '/enter', name: 'EnterNow', description: "Submit your nomination for the AdAura Awards 2026. Multi-category cart, autosave drafts, GST-compliant invoices. Entry f" }),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Enter Now', path: '/enter' }]),
        ]}
      />
      <Toaster position="top-center" toastOptions={{ style: { background: 'var(--bg-deep)', color: 'var(--ink)', border: '1px solid rgba(184, 136, 74, 0.4)', borderRadius: 0 } }} />

      {/* Public /enter route: gate the nomination form behind Registration + OTP.
          Inside the Portal (/portal/new) auth is already enforced by PortalLayout,
          so we skip the gate there. */}
      <section className={portalMode ? "pb-12" : "pt-28 md:pt-32 pb-24"}>
        <div className={portalMode ? "" : "content-container"}>
          {!portalMode && authLoading ? (
            <div className="text-center text-[var(--ink-soft)] text-[11px] tracking-[0.28em] uppercase py-24" data-testid="enter-auth-loading">
              Checking your session…
            </div>
          ) : !portalMode && !nominator ? (
            <RegisterGate />
          ) : !portalMode && nominator ? (
            // Authed user on /enter — redirect happens in useEffect above.
            // Render a lightweight placeholder while the navigation ticks over.
            <div className="text-center text-[var(--ink-soft)] text-[11px] tracking-[0.28em] uppercase py-24" data-testid="enter-redirecting">
              Opening your portal…
            </div>
          ) : success ? (
            <Reveal>
              <div className="border border-[#C9A56C] bg-white p-10 md:p-20 text-center max-w-3xl mx-auto shadow-[0_24px_60px_-30px_rgba(58,36,24,0.25)]" data-testid="form-success">
                <span className="w-14 h-14 inline-flex items-center justify-center border border-[#C9A56C] text-[#C9A56C]"><Check size={22} /></span>
                <h3 className="font-serif text-[var(--ink)] text-3xl md:text-5xl mt-8">Nomination <span className="text-[#C9A56C]">received.</span></h3>
                <p className="text-[var(--ink-muted)] mt-6 max-w-xl mx-auto leading-relaxed">
                  Our team will be in touch within 48 hours with the next steps for your entry.
                </p>
                <button onClick={() => setSuccess(false)} className="btn-ghost mt-10" data-testid="form-submit-another">Submit another entry</button>
              </div>
            </Reveal>
          ) : (
            <form onSubmit={submit} noValidate data-testid="nomination-form" className="max-w-5xl mx-auto bg-white border border-[var(--gold-soft)] shadow-[0_24px_60px_-30px_rgba(58,36,24,0.25)]">
              {/* Portal-mode Back to Dashboard — only shown when this form
                  is rendered inside PortalLayout so the /enter public page
                  does not get an orphan back link that goes nowhere useful. */}
              {portalMode && (
                <div className="border-b border-[var(--gold-soft)] px-6 md:px-12 pt-5 pb-3 bg-white">
                  <button type="button" onClick={() => navigate('/portal/dashboard')}
                          data-testid="form-back-dashboard"
                          className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.28em] uppercase text-[var(--ink-soft)] hover:text-[var(--walnut)]">
                    <ArrowLeft size={13} /> Back to Dashboard
                  </button>
                </div>
              )}
              {/* Form Header — eyebrow + title + welcome copy all live here */}
              <div className="border-b border-[var(--gold-soft)] bg-[var(--bg-deep)]/40 px-6 md:px-12 py-8 md:py-10">
                <div className="text-[10px] tracking-[0.35em] uppercase text-[var(--gold)]">
                  The AdAURA Awards 2026
                </div>
                <h1 className="font-serif text-[var(--ink)] text-3xl md:text-4xl lg:text-5xl tracking-[-0.01em] leading-tight mt-3" data-testid="form-title">
                  Nomination <span className="italic text-[var(--gold)]">Form</span>
                </h1>
                <p className="font-serif text-[var(--ink)]/85 text-[15px] md:text-base leading-[1.7] max-w-3xl mt-5">
                  Welcome to the <span className="text-[var(--gold)]">AdAura Awards 2026</span> nominations. Please complete every section. All entries are reviewed blind by an independent jury.
                </p>
              </div>

              <div className="px-6 md:px-12 py-10 md:py-12 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 lg:gap-12">
                {/* Wave-2 Sidebar navigator — sticky on desktop, inline on mobile */}
                <aside className="lg:sticky lg:top-8 self-start" data-testid="form-section-nav">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-4">Sections</div>
                  {[
                    { i: 1, label: 'Nominee & Entry Details' },
                    { i: 2, label: 'Campaign Information' },
                    { i: 3, label: 'Supporting Documents & Media' },
                  ].map(({ i, label }) => {
                    const done = sectionComplete[i];
                    const active = activeSection === i;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => { setActiveSection(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        data-testid={`section-tab-${i}`}
                        className={`w-full text-left px-3 py-3 mb-1.5 border-l-2 transition-colors ${active ? 'bg-[var(--bg-deep)]/50 border-[var(--gold)] text-[var(--ink)]' : 'border-transparent text-[var(--ink-muted)] hover:border-[var(--gold-soft)] hover:text-[var(--ink)]'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] tracking-normal border ${done ? 'bg-[var(--gold)] text-white border-[var(--gold)]' : active ? 'border-[var(--gold)] text-[var(--gold)]' : 'border-[var(--gold-soft)] text-[var(--ink-muted)]'}`}>
                            {done ? '✓' : i}
                          </span>
                          <span className="text-[12.5px] font-serif leading-tight">{label}</span>
                        </div>
                      </button>
                    );
                  })}
                </aside>

                {/* Active-section panel — all sections stay in state; we
                    conditionally render for a light DOM. On Submit, the
                    global `validate()` still enforces every required
                    field across all three sections. */}
                <div className="min-w-0">
                {/* Legend so entrants can decode the * indicator at a glance. */}
                <div className="mb-6 inline-flex items-center gap-2 bg-[#C2613A]/10 border border-[#C2613A]/40 text-[#C2613A] px-3 py-1.5 text-[10.5px] tracking-[0.2em] uppercase font-semibold" data-testid="required-legend">
                  <span aria-hidden className="text-[13px] font-bold leading-none">*</span>
                  Indicates a required field
                </div>
                {activeSection === 1 && (
                <>
                {/* SECTION 1 */}
              <Section number="01" title="Entrant Information">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                  <div className="md:col-span-2">
                    <SelectField label="Entrant Type" tid="entrant_type" value={form.entrant_type} onChange={(v) => set('entrant_type', v)} options={ENTRANT_TYPES} onBlur={() => validateField('entrant_type')} err={errors.entrant_type} hint="Select the category that best describes the organisation submitting this entry" />
                  </div>
                  {form.entrant_type === 'Other' && (
                    <div className="md:col-span-2">
                      <Field label="Please specify" tid="entrant_type_other" value={form.entrant_type_other} onChange={(v) => set('entrant_type_other', v)} onBlur={() => validateField('entrant_type_other')} err={errors.entrant_type_other} />
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <Field label="Organisation / Agency Name" tid="organisation_name" value={form.organisation_name} onChange={(v) => set('organisation_name', v)} onBlur={() => validateField('organisation_name')} err={errors.organisation_name} />
                  </div>
                  <Field label="First Name" tid="first_name" value={form.first_name} onChange={(v) => set('first_name', v)} onBlur={() => validateField('first_name')} err={errors.first_name} />
                  <Field label="Last Name" tid="last_name" value={form.last_name} onChange={(v) => set('last_name', v)} onBlur={() => validateField('last_name')} err={errors.last_name} />
                  <Field label="Designation" tid="designation" value={form.designation} onChange={(v) => set('designation', v)} onBlur={() => validateField('designation')} err={errors.designation} />
                  <Field label="Email Address" tid="email" type="email" value={form.email} onChange={(v) => set('email', v)} onBlur={() => validateField('email')} err={errors.email} />
                  <Field label="Mobile Number" tid="mobile" value={form.mobile} onChange={(v) => set('mobile', v)} onBlur={() => validateField('mobile')} err={errors.mobile} />
                  <Field label="Organisation Website" tid="website" value={form.website} onChange={(v) => set('website', v)} onBlur={() => validateField('website')} err={errors.website} />
                  <div className="md:col-span-2">
                    <Field label="Company LinkedIn Page" tid="linkedin" value={form.linkedin} onChange={(v) => set('linkedin', v)} optional />
                  </div>
                </div>
              </Section>

              {/* SECTION 2 */}
              <Section number="02" title="Entry Information">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                  <div className="md:col-span-2">
                    <SelectField label="Award Category" tid="category" value={form.category} onChange={(v) => set('category', v)}
                      options={ALL_CATEGORIES.map((c) => c.label)} onBlur={() => validateField('category')} err={errors.category}
                      hint="Select the award category for this nomination. Want to enter more than one category? Save this as a draft — you can create additional nominations from the dashboard and pay for them together." />
                  </div>
                  <Field label="Campaign / Entry Title" tid="entry_title" value={form.entry_title} onChange={(v) => set('entry_title', v)} onBlur={() => validateField('entry_title')} err={errors.entry_title} />
                  <Field label="Brand Name" tid="brand_name" value={form.brand_name} onChange={(v) => set('brand_name', v)} onBlur={() => validateField('brand_name')} err={errors.brand_name} hint="Name of the brand associated with this entry" />
                  <Field label="Campaign Start" tid="campaign_start" type="date" value={form.campaign_start} onChange={(v) => set('campaign_start', v)} onBlur={() => validateField('campaign_start')} err={errors.campaign_start} min="2025-07-01" max={new Date().toISOString().slice(0,10)} hint="Campaign timeline: 1 July 2025 - Till Date" />
                  <Field label="Campaign End" tid="campaign_end" type="date" value={form.campaign_end} onChange={(v) => set('campaign_end', v)} onBlur={() => validateField('campaign_end')} err={errors.campaign_end} min="2025-07-01" max={new Date().toISOString().slice(0,10)} hint="Campaign timeline: 1 July 2025 - Till Date" />
                  <div className="md:col-span-2">
                    <Field label="Primary Market" tid="primary_market" value={form.primary_market} onChange={(v) => set('primary_market', v)} onBlur={() => validateField('primary_market')} err={errors.primary_market} hint="Specify the market or geography targeted by the campaign" />
                  </div>
                </div>
              </Section>
                </>
                )}

                {activeSection === 2 && (
                <>
              {/* SECTION 3 */}
              <Section number="03" title="Campaign Overview">
                <TextArea label="Campaign Summary" tid="campaign_summary" value={form.campaign_summary} onChange={(v) => set('campaign_summary', v)} onBlur={() => validateField('campaign_summary')} err={errors.campaign_summary} max={200} placeholder="A concise overview of the campaign - purpose, target audience and overall approach." />
                <TextArea label="Business Challenge / Opportunity" tid="business_challenge" value={form.business_challenge} onChange={(v) => set('business_challenge', v)} onBlur={() => validateField('business_challenge')} err={errors.business_challenge} max={300} />
                <TextArea label="Strategy & Creative Idea" tid="strategy_idea" value={form.strategy_idea} onChange={(v) => set('strategy_idea', v)} onBlur={() => validateField('strategy_idea')} err={errors.strategy_idea} max={500} placeholder="Describe the strategic thinking and core idea behind the campaign." />
                <TextArea label="Execution & Implementation" tid="execution" value={form.execution} onChange={(v) => set('execution', v)} onBlur={() => validateField('execution')} err={errors.execution} max={500} placeholder="How the campaign was brought to life across channels, platforms and touchpoints." />
                <TextArea label="Why Does This Entry Belong At AdAura?" tid="elevator_pitch" value={form.elevator_pitch} onChange={(v) => set('elevator_pitch', v)} onBlur={() => validateField('elevator_pitch')} err={errors.elevator_pitch} max={100} placeholder="Elevator pitch." />
              </Section>

              {/* SECTION 4 */}
              <Section number="04" title="Impact & Results">
                <TextArea label="Campaign Objectives" tid="campaign_objectives" value={form.campaign_objectives} onChange={(v) => set('campaign_objectives', v)} onBlur={() => validateField('campaign_objectives')} err={errors.campaign_objectives} max={300} />

                <div className="mt-6">
                  <label className="field-label block mb-2">Key Results Snapshot
                    <span className="text-white/30 ml-2 normal-case tracking-normal">- up to 5 bullets</span>
                  </label>
                  <p className="text-white/40 text-[12px] mb-4">Up to 5 bullets summarising the campaign&apos;s most significant achievements.</p>
                  <div className="space-y-3" data-testid="field-key_results">
                    {form.key_results.map((kr, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="numeral text-[#C9A56C] w-6 text-sm">{String(i + 1).padStart(2, '0')}</span>
                        <input
                          type="text"
                          data-testid={`field-key_results-${i}`}
                          value={kr}
                          onChange={(e) => {
                            const next = form.key_results.slice();
                            next[i] = e.target.value;
                            set('key_results', next);
                          }}
                          placeholder={`Result ${i + 1}`}
                          className="field-input"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <TextArea label="Business & Brand Impact" tid="business_impact" value={form.business_impact} onChange={(v) => set('business_impact', v)} onBlur={() => validateField('business_impact')} err={errors.business_impact} max={500} placeholder="Describe the measurable impact - support with data, metrics and outcomes wherever possible." />
              </Section>

              {/* SECTION 5 */}
              <Section number="05" title="Credits & Collaborators">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                  <Field label="Brand Team" tid="credits_brand" value={form.credits_brand} onChange={(v) => set('credits_brand', v)} optional />
                  <Field label="Creative Agency" tid="credits_creative" value={form.credits_creative} onChange={(v) => set('credits_creative', v)} optional />
                  <Field label="Media Agency" tid="credits_media" value={form.credits_media} onChange={(v) => set('credits_media', v)} optional />
                  <Field label="Digital Agency" tid="credits_digital" value={form.credits_digital} onChange={(v) => set('credits_digital', v)} optional />
                  <Field label="Influencer Marketing Agency" tid="credits_influencer" value={form.credits_influencer} onChange={(v) => set('credits_influencer', v)} optional />
                  <Field label="PR & Communications Agency" tid="credits_pr" value={form.credits_pr} onChange={(v) => set('credits_pr', v)} optional />
                  <Field label="Production House / Content Studio" tid="credits_production" value={form.credits_production} onChange={(v) => set('credits_production', v)} optional />
                  <Field label="Technology Partner" tid="credits_tech" value={form.credits_tech} onChange={(v) => set('credits_tech', v)} optional />
                  <div className="md:col-span-2">
                    <Field label="Other Contributing Partners" tid="credits_other" value={form.credits_other} onChange={(v) => set('credits_other', v)} optional />
                  </div>
                </div>
              </Section>
                </>
                )}

                {activeSection === 3 && (
                <>
              {/* SECTION 6 */}
              <Section number="06" title="Supporting Material">
                <FileField
                  label="Case Study Deck · Primary"
                  tid="case_study_deck"
                  hint={`Primary case study — PDF, PPT/PPTX, DOC/DOCX, JPG or PNG. Up to ${DECK_LIMITS_MB.deck} MB.`}
                  url={form.case_study_deck_url}
                  fileName={form.case_study_deck_name}
                  loading={uploading.deck}
                  progress={uploadProgress.deck}
                  onUpload={(file) => upload(file, 'deck')}
                  onClear={() => { set('case_study_deck_url', ''); set('case_study_deck_name', ''); }}
                  accept={DECK_ACCEPT}
                />
                <FileField
                  label="Case Study Deck · Supplementary 1"
                  tid="case_study_deck_2"
                  hint={`Optional supplementary deck. Same file types. Up to ${DECK_LIMITS_MB.deck2} MB.`}
                  url={form.case_study_deck_url_2}
                  fileName={form.case_study_deck_name_2}
                  loading={uploading.deck2}
                  progress={uploadProgress.deck2}
                  onUpload={(file) => upload(file, 'deck2')}
                  onClear={() => { set('case_study_deck_url_2', ''); set('case_study_deck_name_2', ''); }}
                  accept={DECK_ACCEPT}
                  optional
                />
                <FileField
                  label="Case Study Deck · Supplementary 2"
                  tid="case_study_deck_3"
                  hint={`Optional second supplementary deck. Same file types. Up to ${DECK_LIMITS_MB.deck3} MB.`}
                  url={form.case_study_deck_url_3}
                  fileName={form.case_study_deck_name_3}
                  loading={uploading.deck3}
                  progress={uploadProgress.deck3}
                  onUpload={(file) => upload(file, 'deck3')}
                  onClear={() => { set('case_study_deck_url_3', ''); set('case_study_deck_name_3', ''); }}
                  accept={DECK_ACCEPT}
                  optional
                />
                <Field
                  label="Video Link"
                  tid="video_link"
                  value={form.video_link}
                  onChange={(v) => set('video_link', v)}
                  optional
                  hint="YouTube, Vimeo or other publicly viewable video URL."
                />
                {looksPrivate(form.video_link) && (
                  <div className="text-[12px] text-[#C2613A] bg-[#C2613A]/10 border border-[#C2613A]/25 px-3 py-2 -mt-2" data-testid="video-link-private-warn">
                    ⚠ {looksPrivate(form.video_link)}
                  </div>
                )}
                <Field
                  label="Supporting Material Link"
                  tid="supporting_material_link"
                  value={form.supporting_material_link}
                  onChange={(v) => set('supporting_material_link', v)}
                  onBlur={() => validateField('supporting_material_link')} err={errors.supporting_material_link}
                  hint='Google Drive, Dropbox, Vimeo, YouTube, website URL, or any publicly accessible link. Set access to "Anyone with the link can view".'
                  optional
                />
                {looksPrivate(form.supporting_material_link) && (
                  <div className="text-[12px] text-[#C2613A] bg-[#C2613A]/10 border border-[#C2613A]/25 px-3 py-2 -mt-2" data-testid="supporting-link-private-warn">
                    ⚠ {looksPrivate(form.supporting_material_link)}
                  </div>
                )}
                <TextArea
                  label="Previous Recognition"
                  tid="previous_recognition"
                  value={form.previous_recognition}
                  onChange={(v) => set('previous_recognition', v)}
                  max={300}
                  optional
                  placeholder="Award Name · Category · Year. List any prior national or international recognition for this campaign."
                />
              </Section>

              {/* SECTION 7 (Brand Approval Declaration) — removed per product decision.
                  Users are no longer required to upload brand approval or tick a declaration
                  checkbox before submitting. Backend accepts submissions without it. */}
                </>
                )}

              {/* WAVE 3 FOOTER — Autosave indicator + Prev / Next / Save Draft / Continue.
                  When editing a submitted entry, we show "Save Changes" instead. */}
              <div className="mt-14 pt-10 border-t border-[#C9A56C]/20 flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-2 items-center">
                  {activeSection > 1 && (
                    <button type="button" onClick={() => { setActiveSection((s) => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="btn-outline" data-testid="section-prev">
                      ← Previous
                    </button>
                  )}
                  {activeSection < 3 && (
                    <button type="button" onClick={() => { setActiveSection((s) => s + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="btn-outline" data-testid="section-next">
                      Next →
                    </button>
                  )}
                  <span className="text-[10.5px] tracking-[0.24em] uppercase text-[var(--ink-soft)] pl-3" data-testid="autosave-status">
                    {autoSaveStatus === 'saving' ? 'Autosaving…'
                     : autoSaveStatus === 'saved' ? '✓ Draft autosaved'
                     : autoSaveStatus === 'error' ? '⚠ Autosave failed'
                     : ''}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {!isEditingSubmitted && (
                    <button type="button" onClick={() => saveDraft()} disabled={savingDraft || loading}
                      className="btn-outline disabled:opacity-50" data-testid="form-save-draft">
                      {savingDraft ? 'Saving…' : 'Save as Draft'}
                    </button>
                  )}
                  {activeSection === 3 && (
                    <button type="submit" disabled={loading} data-testid="form-submit"
                      className="btn-solid disabled:opacity-50 disabled:cursor-not-allowed">
                      {loading ? (isEditingSubmitted ? 'Saving…' : 'Saving…')
                       : isEditingSubmitted ? (<>Save Changes <Check size={14} /></>)
                       : (<>Save & Go to Cart <ArrowUpRight size={14} /></>)}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-[var(--ink-muted)] text-[12px] leading-relaxed mt-3">
                Each category is an independent nomination. Save this one, then add more from the dashboard — pay for any set of drafts together in a single checkout.
              </p>
              {activeSection === 3 && (
                <p
                  className="text-[var(--ink-muted)] text-[11px] leading-relaxed mt-3 italic"
                  data-testid="submit-declaration-notice"
                >
                  By submitting this nomination, you confirm that the information provided is accurate and that all necessary permissions and approvals have been obtained.
                </p>
              )}

              {/* Need help? — end-of-form contact card (P0 requirement). */}
              <div className="mt-10 border border-[var(--gold-soft)] bg-[var(--bg-alt)] p-6 md:p-7" data-testid="nomination-help-card">
                <div className="text-[10px] tracking-[0.35em] uppercase text-[var(--gold)] mb-2">Need Help?</div>
                <p className="text-[var(--ink)] text-[14px] leading-relaxed">
                  If you&apos;re facing any issues while filling out or submitting your nomination, feel free to contact us.
                </p>
                <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-[13.5px] text-[var(--ink)]">
                  <div className="font-serif text-[var(--ink)]">Atul Dwivedi</div>
                  <a href="mailto:atuldwivedi@marketingmind.in" className="inline-flex items-center gap-2 text-[var(--gold)] hover:text-[var(--walnut)]" data-testid="help-email">
                    <Mail size={13} /> atuldwivedi@marketingmind.in
                  </a>
                  <a href="tel:+919717025198" className="inline-flex items-center gap-2 text-[var(--gold)] hover:text-[var(--walnut)]" data-testid="help-phone">
                    <Phone size={13} /> +91 97170 25198
                  </a>
                </div>
              </div>
                </div>{/* /active-section panel */}
              </div>{/* /padded form body */}
            </form>
          )}
        </div>
      </section>

      {checkoutPreview && (
        <CheckoutModal
          preview={checkoutPreview}
          loading={loading}
          onCancel={() => setCheckoutPreview(null)}
          onConfirm={confirmAndPay}
        />
      )}
    </div>
  );
};

/* ============ Checkout Preview Modal ============ */
const CheckoutModal = ({ preview, loading, onCancel, onConfirm }) => {
  const { submission, payment } = preview;
  const inr = (paise) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto" data-testid="checkout-preview">
      <div className="bg-white border border-[var(--gold-soft)] max-w-lg w-full p-8 shadow-[0_30px_80px_-30px_rgba(58,36,24,0.45)]">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--gold)] mb-2">Order Summary</div>
        <h3 className="font-serif text-2xl text-[var(--ink)]">Confirm your submission</h3>
        <p className="text-[13px] text-[var(--ink-muted)] mt-2 leading-relaxed">
          You&apos;re submitting <strong>{submission.categories.length}</strong> categor{submission.categories.length === 1 ? 'y' : 'ies'} in one submission. One payment covers all entries.
        </p>

        <div className="mt-6 border-t border-[var(--gold-soft)]">
          {payment.line_items.map((li, i) => (
            <div key={i} className="flex items-start justify-between gap-4 py-3 border-b border-[var(--gold-soft)]/60" data-testid={`checkout-line-${i}`}>
              <div className="min-w-0">
                <div className="text-[13px] text-[var(--ink)] leading-snug">{li.category}</div>
                <div className="text-[10.5px] tracking-[0.24em] uppercase text-[var(--ink-soft)] mt-1">Entry fee · GST 18%</div>
              </div>
              <div className="text-right whitespace-nowrap">
                <div className="text-[13px] text-[var(--ink)]">{inr(li.base_paise)}</div>
                <div className="text-[11px] text-[var(--ink-soft)]">+ {inr(li.total_paise - li.base_paise)} GST</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 space-y-1.5">
          <div className="flex justify-between text-[13px] text-[var(--ink-soft)]">
            <span>Subtotal</span>
            <span data-testid="checkout-subtotal">{inr(payment.base_paise)}</span>
          </div>
          <div className="flex justify-between text-[13px] text-[var(--ink-soft)]">
            <span>GST (18%)</span>
            <span data-testid="checkout-gst">{inr(payment.gst_paise)}</span>
          </div>
          <div className="flex justify-between items-baseline pt-3 mt-1 border-t border-[var(--gold-soft)]">
            <span className="text-[11px] tracking-[0.28em] uppercase text-[var(--ink)]">Total Payable</span>
            <span className="font-serif text-2xl text-[var(--walnut)]" data-testid="checkout-total">{inr(payment.amount_paise)}</span>
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <button onClick={onCancel} disabled={loading} data-testid="checkout-cancel"
            className="flex-1 border border-[var(--gold-soft)] text-[var(--ink)] py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[var(--gold-soft)]/10 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} data-testid="checkout-confirm"
            className="flex-2 bg-[var(--walnut)] text-white py-3 px-6 text-[11px] tracking-[0.3em] uppercase disabled:opacity-50">
            {loading ? 'Opening…' : 'Proceed to Pay'}
          </button>
        </div>
        <p className="text-[10.5px] text-[var(--ink-soft)] mt-4 text-center">
          Powered by Razorpay · GST-compliant invoice will be emailed after payment
        </p>
      </div>
    </div>
  );
};

/* ============ Reusable Field Components ============ */

const Section = ({ number, title, subtitle, children }) => (
  <div className="mb-12 md:mb-14 [&:last-of-type]:mb-0">
    <div className="flex items-center gap-4 pb-4 mb-7 border-b border-[var(--gold-soft)]/60">
      <span className="inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 border border-[var(--gold)] text-[var(--gold)] numeral text-sm md:text-base">
        {number}
      </span>
      <div className="flex-1">
        <h2 className="font-serif text-[var(--ink)] text-xl md:text-2xl tracking-[-0.005em] leading-tight">{title}</h2>
        {subtitle && <p className="text-[var(--ink-muted)] text-[12.5px] mt-1 leading-relaxed">{subtitle}</p>}
      </div>
    </div>
    <div>{children}</div>
  </div>
);

const Field = ({ label, tid, value, onChange, onBlur, type = 'text', err, optional, hint, ...rest }) => (
  <div>
    <label className="field-label block mb-3">
      {label}
      {!optional && <span aria-hidden className="text-[#C2613A] ml-1 font-bold text-[13px]">*</span>}
      {optional && <span className="text-white/30 ml-2 normal-case tracking-normal">- Optional</span>}
    </label>
    <input
      data-testid={`field-${tid}`}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className={`field-input ${err ? '!border-[#C2613A] !bg-[#C2613A]/5' : ''}`}
      style={type === 'date' ? { colorScheme: 'light' } : {}}
      aria-required={!optional}
      aria-invalid={!!err}
      {...rest}
    />
    {hint && !err && <p className="text-white/35 text-[11.5px] mt-2 leading-relaxed">{hint}</p>}
    {err && (
      <p className="text-[#C2613A] text-[11.5px] font-semibold tracking-[0.15em] uppercase mt-2 flex items-center gap-1.5" role="alert">
        <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-[#C2613A]" /> {err}
      </p>
    )}
  </div>
);

const SelectField = ({ label, tid, value, onChange, onBlur, options, err, hint, optional }) => (
  <div>
    <label className="field-label block mb-3">
      {label}
      {!optional && <span aria-hidden className="text-[#C2613A] ml-1 font-bold text-[13px]">*</span>}
      {optional && <span className="text-white/30 ml-2 normal-case tracking-normal">- Optional</span>}
    </label>
    <div className="relative">
      <select
        data-testid={`field-${tid}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`field-input appearance-none pr-8 cursor-pointer ${err ? '!border-[#C2613A] !bg-[#C2613A]/5' : ''}`}
        style={{ colorScheme: 'light' }}
        aria-required={!optional}
        aria-invalid={!!err}
      >
        <option value="">Select...</option>
        {options.map((o) => (
          <option key={o} value={o} style={{ background: 'var(--bg)', color: 'var(--ink)' }}>{o}</option>
        ))}
      </select>
      <span aria-hidden className="absolute right-2 top-1/2 -translate-y-1/2 text-[#C9A56C] pointer-events-none">↓</span>
    </div>
    {hint && !err && <p className="text-white/35 text-[11.5px] mt-2 leading-relaxed">{hint}</p>}
    {err && (
      <p className="text-[#C2613A] text-[11.5px] font-semibold tracking-[0.15em] uppercase mt-2 flex items-center gap-1.5" role="alert">
        <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-[#C2613A]" /> {err}
      </p>
    )}
  </div>
);

const MultiSelectField = ({ label, tid, values, onChange, options, max, optional, hint }) => {
  const toggle = (opt) => {
    if (values.includes(opt)) {
      onChange(values.filter((v) => v !== opt));
    } else if (!max || values.length < max) {
      onChange([...values, opt]);
    }
  };
  return (
    <div data-testid={`field-${tid}`}>
      <label className="field-label block mb-3">
        {label}
        {optional && <span className="text-white/30 ml-2 normal-case tracking-normal">- Optional</span>}
        <span className="text-white/30 ml-2 normal-case tracking-normal">
          ({values.length}{max ? `/${max}` : ''} selected)
        </span>
      </label>
      {hint && <p className="text-white/35 text-[11.5px] mb-3 leading-relaxed">{hint}</p>}
      <div className="border border-[#C9A56C]/25 max-h-48 overflow-y-auto">
        {options.map((o) => {
          const checked = values.includes(o);
          const disabled = !checked && max && values.length >= max;
          return (
            <label
              key={o}
              className={`flex items-center gap-3 px-4 py-2.5 border-b border-[#C9A56C]/10 cursor-pointer transition-colors ${
                checked ? 'bg-[#C9A56C]/10' : 'hover:bg-[#C9A56C]/5'
              } ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => toggle(o)}
                className="sr-only"
              />
              <span className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[#C9A56C] border-[#C9A56C]' : 'border-[#C9A56C]/40'}`}>
                {checked && <Check size={11} className="text-black" strokeWidth={3} />}
              </span>
              <span className="text-white/80 text-[13.5px] leading-tight">{o}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const TextArea = ({ label, tid, value, onChange, onBlur, max, err, placeholder, optional }) => {
  const words = wordCount(value);
  const over = words > max;
  const atLimit = words >= max;
  const remaining = max - words;
  const handleChange = (e) => {
    const next = e.target.value;
    // Always allow shrinking (delete / backspace / paste-replace shorter)
    if (next.length <= value.length) {
      onChange(next);
      return;
    }
    // Growing: only allow if we'd still be under the word limit
    const w = wordCount(next);
    if (w > max) {
      // hard cap to first `max` words and ignore any extra typing
      const truncated = next.trim().split(/\s+/).slice(0, max).join(' ');
      onChange(truncated);
      return;
    }
    // Word count is still within limit — but if we're AT the limit and the user
    // is trying to keep adding characters to the last word, block that too.
    if (atLimit && w === max) {
      return;
    }
    onChange(next);
  };
  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between mb-3 gap-3">
        <label className="field-label">
          {label}
          {!optional && <span aria-hidden className="text-[#C2613A] ml-1 font-bold text-[13px]">*</span>}
          {optional && <span className="text-white/30 ml-2 normal-case tracking-normal">- Optional</span>}
          <span className="text-white/40 ml-2 normal-case tracking-normal">- Max {max} words</span>
        </label>
        <span
          className={`text-[11px] tracking-[0.2em] uppercase numeral ${
            over || atLimit ? 'text-[#E0573C]' : remaining <= Math.max(10, Math.floor(max * 0.1)) ? 'text-[#C9A56C]' : 'text-white/40'
          }`}
        >
          {words} / {max} words
        </span>
      </div>
      <textarea
        data-testid={`field-${tid}`}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        onKeyDown={(e) => {
          // When at limit, allow only navigation / delete keys — block all typing
          if (atLimit) {
            const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Shift', 'Control', 'Meta', 'Alt'];
            const isCopyPaste = (e.ctrlKey || e.metaKey) && ['a', 'c', 'x', 'v', 'z'].includes(e.key.toLowerCase());
            if (!allowed.includes(e.key) && !isCopyPaste) {
              e.preventDefault();
            }
          }
        }}
        rows={5}
        placeholder={placeholder}
        className={`field-input resize-y min-h-[140px] ${atLimit ? 'border-[#E0573C]/60' : ''}`}
        aria-invalid={atLimit}
      />
      <p className={`text-[11px] mt-2 ${atLimit ? 'text-[#E0573C]' : 'text-white/40'}`}>
        {atLimit
          ? `Word limit reached. To add more, delete some words first.`
          : `${remaining} ${remaining === 1 ? 'word' : 'words'} remaining`}
      </p>
      {err && (
        <p className="text-[#C2613A] text-[11.5px] font-semibold tracking-[0.15em] uppercase mt-2 flex items-center gap-1.5" role="alert">
          <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-[#C2613A]" /> {err}
        </p>
      )}
    </div>
  );
};

const FileField = ({ label, tid, hint, url, fileName, loading, progress, onUpload, onClear, accept, optional }) => {
  const inputRef = useRef(null);
  return (
    <div className="mb-6">
      <label className="field-label block mb-3">
        {label}
        {!optional && <span aria-hidden className="text-[#C2613A] ml-1 font-bold text-[13px]">*</span>}
        {optional && <span className="text-white/30 ml-2 normal-case tracking-normal">- Optional</span>}
      </label>
      {hint && <p className="text-white/40 text-[12px] mb-4 leading-relaxed max-w-2xl">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        data-testid={`field-${tid}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = '';
        }}
      />
      {url ? (
        <div className="flex items-center gap-3 border border-[var(--gold-soft)] bg-[var(--bg-alt)] px-5 py-4">
          <FileText size={16} className="text-[var(--gold)]" />
          <a
            href={`${BACKEND_URL}${url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--ink)] hover:text-[var(--gold)] text-sm truncate flex-1"
          >
            {fileName || 'Uploaded file'}
          </a>
          <button
            type="button"
            onClick={onClear}
            className="text-[var(--ink-muted)] hover:text-[var(--gold)]"
            data-testid={`field-${tid}-clear`}
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="w-full md:w-auto">
          <button
            type="button"
            disabled={loading}
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-3 border border-dashed border-[var(--gold-soft)] hover:border-[var(--gold)] bg-transparent px-5 py-4 text-[var(--ink-muted)] hover:text-[var(--gold)] transition-colors text-sm w-full md:w-auto disabled:opacity-50"
            data-testid={`field-${tid}-trigger`}
          >
            <Upload size={14} />
            {loading
              ? (typeof progress === 'number' && progress > 0 && progress < 100
                  ? `Uploading… ${progress}%`
                  : 'Uploading…')
              : 'Choose file (PDF / DOCX / PPTX / Image · max 15 MB)'}
          </button>
          {loading && typeof progress === 'number' && progress > 0 && (
            <div className="mt-2 h-[3px] w-full bg-[var(--gold-soft)]/30" data-testid={`field-${tid}-progress`}>
              <div className="h-full bg-[var(--gold)] transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnterNow;
